import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { OMSView } from '@src/components/OMS/OMSView';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import type { BlotterTableRow, CareOrder, MinimalSubscriptionResponse, Quote } from '@talos/kyoko';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  EMPTY_ARRAY,
  EMPTY_OBJECT,
  ExportMenuItems,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  Toggle,
  useAccordionFilterBuilder,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useDateRangeFilter,
  useDisclosureWithContext,
  useMixpanel,
  usePersistedBlotterTable,
  type BlotterTableFilter,
  type Column,
} from '@talos/kyoko';
import type { GridOptions } from 'ag-grid-community';
import type { RowDoubleClickedEvent } from 'ag-grid-enterprise';
import { useCallback, useMemo, useState } from 'react';
import type { Observable } from 'rxjs';
import styled from 'styled-components';
import { primeNewRFQForm, showRFQ } from '../../../components/OMS/NewRFQ/RFQSlice';
import { careOrderToPrimeRFQParams } from '../../../components/OMS/NewRFQ/utils';
import { openView } from '../../../components/OMS/OMSSlice';
import { useFeatureFlag } from '../../../hooks';
import { CareOrderBlotterHUD } from './CareOrderBlotterHUD';
import { CareOrderNetButton } from './CareOrderNetButton';
import { IncludeOpenCareOrdersOutsideDateRangeToggle } from './IncludeOpenCareOrdersOutsideDateRangeToggle';
import { ValidateFixingDialog } from './ValidateFixing/ValidateFixingDialog';
import { getCareOrderExportOptions } from './careOrderExportOptions';
import { CARE_ORDER_STATUS_AGG_FUNC, careOrderStatusAggFunc } from './columns/CareOrderBlotterEntityStatusColumn';
import { CARE_ORDER_PROGRESS_AGG_FUNC, careOrderProgressAggFunc } from './columns/careOrderProgressColumn';
import { useCareOrderBlotterAutoGroupColDef } from './columns/useCareOrderBlotterAutoGroupColDef';
import { isCareOrderRow, isQuoteRow, type CareOrderBlotterEntity } from './types';
import { useCareOrderBlotterDataObs } from './useCareOrderBlotterDataObs';
import { useCareOrderColumns } from './useCareOrderColumns';
import {
  careOrderTimestampFields,
  useCareOrderFilter,
  type CareOrderFilter,
  type CareOrderTimestampField,
} from './useCareOrderFilter';
import { getSelectedCareOrderRowsFromNodes, useCareOrderMenu } from './useCareOrderMenu';
import { useNetCareOrdersMenuItem } from './useNetCareOrdersMenuItem';

function getDataPath(row: CareOrderBlotterEntity): string[] {
  return row.dataPath;
}

interface CareOrdersTabData {
  showAllDecimals?: boolean;
  expandAllGroups?: boolean;
  includeOpenCareOrdersOutsideDateRange?: boolean;
}

export interface CareOrderBlotterParams {
  blotterID: string;
  portalId?: string;
  defaultFilter?: BlotterTableFilter;
  defaultColumns?: (keyof CareOrder | Partial<Column>)[];
  showHUD?: boolean;
  tabData?: CareOrdersTabData;
  onUpdateTabData?: (data: CareOrdersTabData) => void;
  onCloneTab?: () => void;
}

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  // Always show the left-pinned line
  && .ag-pinned-left-cols-container,
  && .ag-pinned-left-header {
    border-right: 1px solid var(--ag-border-color);
  }
`;

export function CareOrderBlotter({
  blotterID,
  portalId,
  defaultColumns,
  defaultFilter,
  showHUD,
  tabData,
  onUpdateTabData,
  onCloneTab,
}: CareOrderBlotterParams) {
  const mixpanel = useMixpanel();
  const dispatch = useAppStateDispatch();
  const { showAllDecimals, includeOpenCareOrdersOutsideDateRange = true } = tabData ?? EMPTY_OBJECT;
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const {
    enableCareOrderNetting,
    enableFlatCareOrderBlotterExport,
    enableTakerValidateFixing,
    careOrderExportMakerList,
  } = useFeatureFlag();

  const onSendRFQ = useCallbackRef((entity: CareOrder) => {
    dispatch(openView(OMSView.RFQForm));
    dispatch(primeNewRFQForm(careOrderToPrimeRFQParams(entity)));
  });

  const onShowRFQ = useCallbackRef((entity: Quote) => {
    dispatch(openView(OMSView.RFQForm));
    dispatch(showRFQ(entity));
  });

  const { openDetailsDrawer } = useDetailsDrawer();
  const onShowTimeline = useCallbackRef((entity: CareOrderBlotterEntity) => {
    if (isQuoteRow(entity) || isCareOrderRow(entity)) {
      openDetailsDrawer({ entity });
    }
  });

  const defaultCareOrderColumns = useCareOrderColumns({ defaultColumns, onSendRFQ, onShowRFQ, onShowTimeline });
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });
  const enableCareOrderExportMakerColumns = careOrderExportMakerList.length > 0;

  const handleDoubleClickRow = useCallbackRef(({ data }: RowDoubleClickedEvent<CareOrderBlotterEntity>) => {
    if (isQuoteRow(data) || isCareOrderRow(data)) {
      openDetailsDrawer({ entity: data });
    }
  });

  const careOrderFilter = useCareOrderFilter({
    initialFilter: persistedBlotterTable.initialState?.filter,
    saveFilter: persistedBlotterTable.onFilterChanged,
    includeOpenCareOrdersOutsideDateRange,
  });

  const dateRangeFilter = useDateRangeFilter<CareOrderTimestampField, CareOrderFilter>(
    careOrderFilter.filter,
    careOrderFilter.changeFilter
  );
  const { dataObservable, errorObservable } = useCareOrderBlotterDataObs(enableCareOrderNetting);
  const [selectedCareOrders, setSelectedCareOrders] = useState<CareOrder[]>([]);

  const netCareOrdersMenu = useNetCareOrdersMenuItem<CareOrder>();
  const { filterBuilderProps } = careOrderFilter;
  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const careOrderMenu = useCareOrderMenu({
    filterableProperties: careOrderFilter.filterableProperties,
    openClause: filterBuilderAccordion.openClause,
    onSendRFQ,
    netCareOrdersMenu,
  });

  const autoGroupColumnDef = useCareOrderBlotterAutoGroupColDef();
  const gridOptions: GridOptions<CareOrderBlotterEntity> = useMemo(
    () => ({
      onRowDoubleClicked: handleDoubleClickRow,
      showOpenedGroup: true,
      autoGroupColumnDef,
      groupDefaultExpanded: tabData?.expandAllGroups ? -1 : undefined,
      treeData: true,
      getDataPath,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems: careOrderMenu.getContextMenuItems,
      onSelectionChanged: event => {
        setSelectedCareOrders(getSelectedCareOrderRowsFromNodes(event.api.getSelectedNodes()));
      },
      aggFuncs: {
        [CARE_ORDER_STATUS_AGG_FUNC]: careOrderStatusAggFunc,
        [CARE_ORDER_PROGRESS_AGG_FUNC]: careOrderProgressAggFunc,
      },
      suppressAggFuncInHeader: true,
      processUnpinnedColumns: () => EMPTY_ARRAY,
    }),
    [handleDoubleClickRow, careOrderMenu.getContextMenuItems, autoGroupColumnDef, tabData?.expandAllGroups]
  );

  const exportOptions = useMemo(() => {
    return getCareOrderExportOptions({
      enableFlatCareOrderBlotterExport,
      enableCareOrderExportMakerColumns,
    });
  }, [enableFlatCareOrderBlotterExport, enableCareOrderExportMakerColumns]);

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const blotterTable = useBlotterTable<CareOrderBlotterEntity>({
    dataObservable,
    errorObservable,
    rowID: 'rowID',
    persistence: persistedBlotterTable,
    clientLocalFilter: careOrderFilter.clientSideFilter,
    columns: defaultCareOrderColumns,
    gridOptions,
    exportOptions,
    settings: blotterTableSettings,
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleExpandAllGroups = useCallback(() => {
    expandAllGroups();
    onUpdateTabData?.({ expandAllGroups: true });
  }, [expandAllGroups, onUpdateTabData]);

  const handleCollapseAllGroups = useCallback(() => {
    collapseAllGroups();
    onUpdateTabData?.({ expandAllGroups: false });
  }, [collapseAllGroups, onUpdateTabData]);

  const validateFixingDialog =
    useDisclosureWithContext<Observable<MinimalSubscriptionResponse<CareOrderBlotterEntity>>>();

  const handleShowAllDecimalsChange = useCallback(
    (value: boolean) => {
      onUpdateTabData?.({ showAllDecimals: value });
    },
    [onUpdateTabData]
  );
  const handleIncludeOpenCareOrdersOutsideDateRangeChange = useCallback(
    (value: boolean) => {
      onUpdateTabData?.({ includeOpenCareOrdersOutsideDateRange: value });
    },
    [onUpdateTabData]
  );
  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <Wrapper>
      <BlotterTableFilters
        portalId={portalId}
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        {...dateRangeFilter}
        timestampFields={careOrderTimestampFields}
        quickFilterPrefix={
          enableCareOrderNetting ? (
            <CareOrderNetButton selectedCareOrders={selectedCareOrders} netCareOrdersMenu={netCareOrdersMenu} />
          ) : undefined
        }
        suffix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={handleExpandAllGroups}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={handleCollapseAllGroups}
              data-testid="collapse-all-button"
            />
            <Divider orientation="vertical" mx="spacing4" />
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              {!showAllDecimalsInBlotters && onUpdateTabData && (
                <Toggle
                  size={FormControlSizes.Small}
                  checked={showAllDecimals ?? false}
                  onChange={handleShowAllDecimalsChange}
                  label="Show All Decimals"
                />
              )}
              {onUpdateTabData && (
                <IncludeOpenCareOrdersOutsideDateRangeToggle
                  checked={includeOpenCareOrdersOutsideDateRange}
                  onChange={handleIncludeOpenCareOrdersOutsideDateRangeChange}
                />
              )}
              {enableTakerValidateFixing && (
                <Button
                  size={FormControlSizes.Small}
                  variant={ButtonVariants.Default}
                  startIcon={IconName.ShieldCheck}
                  onClick={() => validateFixingDialog.open(dataObservable)}
                  data-testid="validate-fixing-button"
                >
                  Validate Fixing
                </Button>
              )}
              {onCloneTab && (
                <Button
                  startIcon={IconName.Duplicate}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  onClick={handleCloneTab}
                >
                  Clone Tab
                </Button>
              )}
              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            </BlotterTableExtrasMenu>
          </>
        }
      />
      {showHUD && <CareOrderBlotterHUD blotterTable={blotterTable} getCareOrder={getCareOrderFromBlotterTableRow} />}
      <BlotterTable {...blotterTable} />
      {careOrderMenu.dialogs}
      <ValidateFixingDialog {...validateFixingDialog} />
    </Wrapper>
  );
}

function getCareOrderFromBlotterTableRow(row: BlotterTableRow<CareOrderBlotterEntity>) {
  return row.data != null && isCareOrderRow(row.data) ? row.data : undefined;
}
